import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { 
  ArrowLeft, Star, RefreshCw, User, MessageSquare, Calendar
} from "lucide-react";

const StarRating = ({ value, onChange, size = "md", readonly = false }) => {
  const [hovered, setHovered] = useState(0);
  const sizeClass = size === "lg" ? "h-8 w-8" : size === "sm" ? "h-4 w-4" : "h-6 w-6";
  
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(star => (
        <button
          key={star}
          type="button"
          disabled={readonly}
          onClick={() => !readonly && onChange?.(star)}
          onMouseEnter={() => !readonly && setHovered(star)}
          onMouseLeave={() => setHovered(0)}
          className={`${readonly ? 'cursor-default' : 'cursor-pointer'}`}
        >
          <Star 
            className={`${sizeClass} transition-colors ${
              (hovered || value) >= star 
                ? 'text-yellow-500 fill-yellow-500' 
                : 'text-gray-400'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

const RatingsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [driverFilter, setDriverFilter] = useState("");
  const [drivers, setDrivers] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [ratingsRes, driversRes] = await Promise.all([
        api.get("/ratings"),
        api.get("/users/drivers")
      ]);
      setRatings(ratingsRes.data);
      setDrivers(driversRes.data);
    } catch (error) {
      toast.error("Failed to load ratings");
    } finally {
      setLoading(false);
    }
  };

  const filteredRatings = driverFilter 
    ? ratings.filter(r => r.driver_id === driverFilter)
    : ratings;

  const getAverageRating = () => {
    if (filteredRatings.length === 0) return 0;
    const sum = filteredRatings.reduce((acc, r) => acc + r.rating, 0);
    return (sum / filteredRatings.length).toFixed(1);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass sticky top-0 z-10 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/admin")} data-testid="back-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold">Customer Feedback</h1>
              <p className="text-sm text-muted-foreground">View and manage customer ratings</p>
            </div>
          </div>
          <Button variant="outline" onClick={fetchData} data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="dashboard-card" data-testid="total-reviews-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-yellow-500/10 flex items-center justify-center">
                  <Star className="h-7 w-7 text-yellow-500 fill-yellow-500" />
                </div>
                <div>
                  <p className="text-3xl font-bold">{filteredRatings.length}</p>
                  <p className="text-sm text-muted-foreground">Total Reviews</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="dashboard-card" data-testid="avg-rating-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-green-500/10 flex items-center justify-center">
                  <Star className="h-7 w-7 text-green-500 fill-green-500" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-3xl font-bold">{getAverageRating()}</p>
                    <span className="text-xl text-muted-foreground">/5</span>
                  </div>
                  <p className="text-sm text-muted-foreground">Average Rating</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="dashboard-card">
            <CardContent className="p-6">
              <p className="text-sm text-muted-foreground mb-3">Filter by Driver</p>
              <select 
                value={driverFilter}
                onChange={(e) => setDriverFilter(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-black/5 border border-black/10"
                data-testid="driver-filter"
              >
                <option value="">All Drivers</option>
                {drivers.map(driver => (
                  <option key={driver.id} value={driver.id}>{driver.name}</option>
                ))}
              </select>
            </CardContent>
          </Card>
        </div>

        {/* Ratings List */}
        <Card className="dashboard-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-primary" />
              Customer Reviews
            </CardTitle>
          </CardHeader>
          <CardContent>
            {filteredRatings.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Star className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No ratings yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredRatings.map(rating => (
                  <div key={rating.id} className="p-4 rounded-lg bg-black/5 border border-white/5" data-testid={`rating-${rating.id}`}>
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                            <User className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium">{rating.client_name}</p>
                            <p className="text-xs text-muted-foreground">
                              Booking: <span className="text-primary">{rating.booking_ref}</span>
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-4 mb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">Overall:</span>
                            <StarRating value={rating.rating} readonly size="sm" />
                          </div>
                          {rating.driver_rating && (
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-muted-foreground">Driver:</span>
                              <StarRating value={rating.driver_rating} readonly size="sm" />
                            </div>
                          )}
                          {rating.punctuality_rating && (
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-muted-foreground">Punctuality:</span>
                              <StarRating value={rating.punctuality_rating} readonly size="sm" />
                            </div>
                          )}
                          {rating.vehicle_rating && (
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-muted-foreground">Vehicle:</span>
                              <StarRating value={rating.vehicle_rating} readonly size="sm" />
                            </div>
                          )}
                        </div>
                        
                        {rating.feedback && (
                          <p className="text-sm bg-black/5 p-3 rounded-lg italic">&ldquo;{rating.feedback}&rdquo;</p>
                        )}
                      </div>
                      
                      <div className="text-right text-sm">
                        {rating.driver_name && (
                          <p className="text-muted-foreground">
                            Driver: <span className="text-foreground">{rating.driver_name}</span>
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          <Calendar className="h-3 w-3 inline mr-1" />
                          {new Date(rating.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

// Rating submission component for clients
export const RatingSubmitForm = ({ bookingId, onSuccess }) => {
  const [rating, setRating] = useState(0);
  const [driverRating, setDriverRating] = useState(0);
  const [punctualityRating, setPunctualityRating] = useState(0);
  const [vehicleRating, setVehicleRating] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0) {
      toast.error("Please select an overall rating");
      return;
    }

    try {
      setSubmitting(true);
      await api.post("/ratings", {
        booking_id: bookingId,
        rating,
        driver_rating: driverRating || null,
        punctuality_rating: punctualityRating || null,
        vehicle_rating: vehicleRating || null,
        feedback: feedback || null
      });
      toast.success("Thank you for your feedback!");
      onSuccess?.();
    } catch (error) {
      const msg = error.response?.data?.detail || "Failed to submit rating";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="block text-sm font-medium mb-2">Overall Experience *</label>
        <StarRating value={rating} onChange={setRating} size="lg" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium mb-2">Driver</label>
          <StarRating value={driverRating} onChange={setDriverRating} />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">Punctuality</label>
          <StarRating value={punctualityRating} onChange={setPunctualityRating} />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2">Vehicle</label>
          <StarRating value={vehicleRating} onChange={setVehicleRating} />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">Additional Comments (Optional)</label>
        <Textarea
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder="Tell us about your experience..."
          rows={4}
          data-testid="feedback-input"
        />
      </div>

      <Button type="submit" disabled={submitting} className="w-full" data-testid="submit-rating-btn">
        {submitting ? "Submitting..." : "Submit Review"}
      </Button>
    </form>
  );
};

export default RatingsPage;
